/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IComponentProps } from '@msdyn365-commerce/core';
import { CommerceException } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Alert, Button } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import { moduleClassName } from '../eve-business-partners';
import { IEveBusinessPartnersResources } from '../eve-business-partners.props.autogenerated';
// import { SearchBar } from './search-bar';

/**
 * Props of DataGrid component.
 */
export interface IDataGridProps<Tdata> extends IComponentProps {
    className: string;
    pageSize?: number;
    searchPlaceholderLabel: string;
    defaultSorting?: ISortingInfo;
    resources: IEveBusinessPartnersResources;
    columns: IDataGridColumn<Tdata>[];
    loadData(): Promise<Tdata[]>;
}

/**
 * State of DataGrid component.
 */
export interface IDataGridState<Tdata> {
    data: Tdata[];
    paging: IPagingInfo;
    filter: IFilterInfo;
    sorting?: ISortingInfo;
    isLoading: boolean;
    errorMessage: string;
}

export interface IPagingInfo {
    index: number;
}

export interface IFilterInfo {
    searchText: string;
}

export interface ISortingInfo {
    fieldName: string;
    ascending: boolean;
}

/**
 * Column definition of DataGrid component.
 */
export interface IDataGridColumn<Tdata> {
    /**
     * A unique key for identifying the column.
     */
    reactKey: string;

    /**
     * Name to render on the column header.
     */
    name: string;

    /**
     * Class name added to table cell.
     */
    className?: string;

    /**
     * The field to pull the text value from for the column.Can be unset if a custom `onRender` method is provided.
     */
    fieldName?: string;

    /**
     * Whether the user can search this column by text.
     */
    isSearchable?: boolean;

    /**
     * Custom renderer for cell content, instead of the default text rendering.
     */
    onRender?(item: Tdata): JSX.Element;

    /**
     * Event when the table cell is clicked.
     */
    onClick?(item: Tdata): void;
}

const defaultPageSize = 10;

/**
 * DataGrid component encapsulates common table functionalities.
 */
export class DataGrid<Tdata> extends React.Component<IDataGridProps<Tdata>, IDataGridState<Tdata>> {
    constructor(props: IDataGridProps<Tdata>) {
        super(props);
        this.state = {
            data: [],
            sorting: props.defaultSorting,
            paging: { index: 0 },
            filter: { searchText: '' },
            isLoading: true,
            errorMessage: ''
        };
    }

    public componentDidMount(): void {
        this.loadDataInternal();
    }

    public shouldComponentUpdate(nextProps: Readonly<IDataGridProps<Tdata>>, nextState: Readonly<IDataGridState<Tdata>>): boolean {
        return (
            this.props.id !== nextProps.id ||
            this.props.columns !== nextProps.columns ||
            this.state.data !== nextState.data ||
            this.state.paging !== nextState.paging ||
            this.state.filter !== nextState.filter ||
            this.state.isLoading !== nextState.isLoading ||
            this.state.errorMessage !== nextState.errorMessage
        );
    }

    public render(): JSX.Element | null {
        const data = this.getDataToDisplay();
        const hasMore = this.hasMore();
        if (this.state.errorMessage) {
            return <Alert color='danger'>{this.state.errorMessage}</Alert>;
        }
        if (this.state.isLoading) {
            return <span className={`${moduleClassName}__loading`}>{this.props.resources.loadingLabel}</span>;
        }
        if (!ArrayExtensions.hasElements(data)) {
            return (
                <>
                    {/*<SearchBar {...this.props} onSearch={this.onSearch} />*/}
                    <p className={`${moduleClassName}__empty-message`}>{this.props.resources.noDataMessage}</p>
                </>
            );
        }
        return (
            <>
                {/*<SearchBar {...this.props} onSearch={this.onSearch} />*/}
                <table className={this.props.className}>
                    {this.renderGridHeader()}
                    {data.map((item, index) => this.renderGridRow(item, index))}
                </table>
                {hasMore && (
                    <div className={`${this.props.className}__load-more`}>
                        <Button onClick={this.loadMore} className={`${this.props.className}__load-more-btn`}>
                            {this.props.resources.businessPartnerLoadMore}
                        </Button>
                    </div>
                )}
            </>
        );
    }

    /**
     * Renders the TH header in the table.
     * @returns - Header react node.
     */
    private readonly renderGridHeader = () => {
        const className = `${this.props.className}__header`;
        const rowClassName = `${className}__row`;
        return (
            <thead className={className}>
                <tr className={rowClassName}>
                    {this.props.columns.map(column => (
                        <th key={column.reactKey} className={column.className}>
                            {column.name}
                        </th>
                    ))}
                </tr>
            </thead>
        );
    };

    /**
     * Renders table row.
     * @param item - Data item for this row.
     * @param index - Index of the row.
     * @returns - Row react node.
     */
    private readonly renderGridRow = (item: Tdata, index: number) => {
        const rowClassName = `${this.props.className}__row`;
        return (
            <tr className={rowClassName} key={index}>
                {this.props.columns.map(column => {
                    return (
                        <td key={column.reactKey} className={column.className} onClick={() => column.onClick?.(item)}>
                            {column.fieldName ? (
                                <span>{((item as unknown) as { [key: string]: string })[column.fieldName]}</span>
                            ) : (
                                column.onRender?.(item)
                            )}
                        </td>
                    );
                })}
            </tr>
        );
    };

    /**
     * Load data for the table.
     * @returns Promise<void>.
     */
    private readonly loadDataInternal = async () => {
        if (!this.props.context.actionContext.requestContext.user.isAuthenticated) {
            this.setState({
                isLoading: false
            });
            return;
        }
        try {
            const result = await this.props.loadData();
            this.setState({
                isLoading: false,
                data: result
            });
        } catch (e) {
            this.setState({
                isLoading: false,
                errorMessage: (e as { data: CommerceException })?.data?.LocalizedMessage || this.props.resources.genericErrorMessage
            });
        }
    };

    // private readonly onSearch = (searchText: string): void => {
    //     this.setState({
    //         paging: { index: 0 },
    //         filter: { searchText }
    //     });
    // };

    private readonly loadMore = (): void => {
        this.setState({
            paging: { index: this.state.paging.index + 1 }
        });
    };

    private readonly sortData = (): Tdata[] => {
        const sorting = this.state.sorting;
        if (!sorting) {
            return this.state.data;
        }
        const { fieldName, ascending } = sorting;
        return this.state.data.sort((item1: Tdata, item2: Tdata) => {
            let value1 = ((item1 as unknown) as { [key: string]: string })[fieldName];
            let value2 = ((item2 as unknown) as { [key: string]: string })[fieldName];
            if (typeof value1 === 'string' && typeof value2 === 'string') {
                value1 = value1.toLowerCase();
                value2 = value2.toLowerCase();
            }
            const compareResult = value1 > value2 ? 1 : value1 < value2 ? -1 : 0;
            return ascending ? compareResult : compareResult * -1;
        });
    };

    private readonly filterData = (): Tdata[] => {
        const column = this.props.columns.find(c => c.isSearchable);
        const searchText = this.state.filter.searchText;
        return this.sortData().filter(item => {
            if (!column?.fieldName || !searchText) {
                return true;
            }
            const columnValue = ((item as unknown) as { [key: string]: string })[column.fieldName];
            return columnValue && columnValue.toLowerCase().indexOf(searchText.trim().toLowerCase()) >= 0;
        });
    };

    private readonly getDataToDisplay = (): Tdata[] => {
        const itemsCount = (this.props.pageSize || defaultPageSize) * (this.state.paging.index + 1);
        const filteredItems = this.filterData();
        return filteredItems.slice(0, itemsCount);
    };

    private readonly hasMore = (): boolean => {
        const itemsCount = (this.props.pageSize || defaultPageSize) * (this.state.paging.index + 1);
        const filteredItems = this.filterData();
        return filteredItems.length > itemsCount;
    };
}
